import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, TitleStrategy, withViewTransitions } from '@angular/router';
import { animateOnlyBetweenViews } from './core/utils/view-transitions';

import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { createQueryClient } from './core/query/query-client';
import { invalidateOnWriteInterceptor } from './core/query/invalidate-on-write.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { routes } from './app.routes';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { translationInitFactory } from './core/i18n/translation-init';
import { TranslatedTitleStrategy } from './core/i18n/translated-title.strategy';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthInterceptor } from './core/interceptor/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { CsrfService } from './core/services/csrf.service';
import { TrackedDialogService } from './core/services/tracked-dialog.service';
import { InactivityLogoutService } from './core/services/inactivity-logout.service';
import { sanitizeAuthStorage } from './core/utils/auth-storage';
import { firstValueFrom } from 'rxjs';

// Translation files are static assets: load them with a client that bypasses the interceptors.
// Going through AuthInterceptor creates a DI cycle at startup
// (TranslateService -> HttpClient -> AuthInterceptor -> AuthService -> Router -> TitleStrategy -> TranslateService).
export function HttpLoaderFactory(handler: HttpBackend) {
  return new TranslateHttpLoader(new HttpClient(handler), './assets/i18n/', '.json');
}

/**
 * Runs once at startup. Re-hydrates in-memory session state and primes the CSRF
 * token from the backend. If the user has no active session (401), all local
 * state is cleared and the browser is hard-redirected to /auth.
 */
const PUBLIC_ROUTES = ['/auth', '/set-password', '/forgot-password', '/reset-password'];

function sessionInitFactory(
  authService: AuthService,
  csrfService: CsrfService,
  inactivityLogoutService: InactivityLogoutService
): () => Promise<void> {
  return async () => {
    sanitizeAuthStorage();

    const path = window.location.pathname;
    if (PUBLIC_ROUTES.some((r) => path.startsWith(r))) {
      return;
    }

    try {
      await firstValueFrom(authService.loadUserInfo());
      await csrfService.fetchToken();
      inactivityLogoutService.startWatching();
    } catch {
      // 401 or any network error: clear all local state and hard-redirect to
      // the login page.  We use window.location.replace() instead of the
      // Angular router because APP_INITIALIZER runs before the router is
      // fully bootstrapped, so navigateByUrl() would be silently dropped.
      authService.clearSessionState(false);
      window.location.replace('/auth');
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
    ),
    // Animated switch between the employees Cards and List views; other navigations are not animated.
    provideRouter(routes, withViewTransitions({ onViewTransitionCreated: animateOnlyBetweenViews })),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([invalidateOnWriteInterceptor])),
    provideTanStackQuery(createQueryClient()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: sessionInitFactory,
      deps: [AuthService, CsrfService, InactivityLogoutService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: translationInitFactory,
      deps: [TranslateService],
      multi: true,
    },
    MessageService,
    DynamicDialogConfig,
    {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpBackend],
    },
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
      defaultLanguage: 'en',
    }).providers!,
    ConfirmationService,
    { provide: DialogService, useClass: TrackedDialogService },
  ],
};
